import { navigate } from 'gatsby';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from '@reach/router';
import lodashGet from 'lodash/get';
import { api, apiHolder, apis, util, getIn18Text, inWindow, } from 'api';
import { message, Spin } from 'antd';
import { GlobalActions, useAppDispatch, AutoReplyActions, useActions, useAppSelector, AutoUnmountReducer } from '@web-common/state/createStore';
import { JumpOutTradeAcquisitionIcon, JumpOutMailMarketingIcon, JumpOutWaimaoWebsiteIcon, JumpOutMediaMarketingIcon, JumpOutCustomBigDataIcon, } from '@web-common/components/UI/Icons/icons';
import SiriusMessage from '@web-common/components/UI/Message/SiriusMessage';
import { getJumpOutRedirectUrl, getWaimaoEntryHost, getWaimaoTrailJumpUrl, isWebmail, navigateWithSearchQuery } from '@web-common/utils/utils';
import ErrorBoundary from '@web-common/hooks/ErrorBoundary';
import LayoutPerf from '@web-common/hooks/LayoutPerf';
import SiriusModal from '@web-common/components/UI/Modal/SiriusModal';
import { isMatchUnitableCrmHash } from '@web-unitable-crm/api/helper';
import { useEventObserver } from '@web-common/hooks/useEventObserver';
import { L2cCrmPageType, L2cCrmPageTypeSet } from '@web-common/conf/waimao/l2c-crm-constant';
import { GlobalAdSpace } from '@web-apps/pages/Home/GlobalAdSpace';
import { isMatchCustomerManageRoute, isMatchCustomerPerformanceRoute } from '@web-common/hooks/use-l2c-crm-menu-data';
import NewbieTaskPopup from '@web-common/components/NewbieTask';
import { getIsSomeMenuVisbleSelector } from '@web-common/state/reducer/privilegeReducer';
import JumpOutModal from '@/components/Layout/JumpOutModal';
import { TAB_STR_SEP } from '@/layouts/Main/util';
import useFeatureVisibility from '@web-common/hooks/useFeatureVisibility';
import SideTabBar from './sideBarWaimao';
import style from './main.module.scss';
const loginApi = api.requireLogicalApi(apis.loginApiImpl);
const trackApi = apiHolder.api.requireLogicalApi(apis.dataTrackerApiImp);
const eventApi = apiHolder.api.getEventApi();
const autoReplyApi = apiHolder.api.requireLogicalApi(apis.autoReplyApiImpl);
const accountApi = apiHolder.api.requireLogicalApi(apis.accountApiImpl);
const systemApi = api.getSystemApi();
const dataStoreApi = apiHolder.api.getDataStoreApi();
const { setScreenCaptureAccess } = apiHolder.api.getSystemApi();
const isElectron = systemApi.isElectron();
const isLingxi = process.env.BUILD_ISLINGXI;
const isLingxiWeb = isLingxi && !isElectron;
const TITLE_PREFIX = process.env.BUILD_ISEDM ? getIn18Text('WANGYIWAIMAOTONG') : getIn18Text('WANGYILINGXIBAN');
if (isElectron) {
    window.electronLib.ipcChannelManage.receiveIpcMain({
        channel: 'capture-screen-message',
        listener: ({ type, text }) => {
            if (type === 'error')
                message.error(text);
            if (type === 'succ')
                message.success(text);
        },
    });
    let accessShow = false;
    window.electronLib.ipcChannelManage.receiveIpcMain({
        channel: 'get-screen-access',
        listener: () => {
            if (accessShow)
                return;
            accessShow = true;
            SiriusModal.confirmError({
                title: '网易灵犀办公需要截图权限',
                content: '请前往“系统偏好设置-安全性与隐私-屏幕录制”勾选网易灵犀办公，开启截图权限',
                centered: true,
                onOk() {
                    console.log('OK');
                    setScreenCaptureAccess();
                    accessShow = false;
                },
                okText: '前往设置',
                onCancel() {
                    accessShow = false;
                },
            });
        },
    });
}
const EXTRA_TABS = [
    {
        activeKey: 'jumpOut_acquisition',
        icon: JumpOutTradeAcquisitionIcon,
        tag: getIn18Text('WAIMAOHUOKE'),
        name: 'jumpOut_acquisition',
    },
    {
        activeKey: 'jumpOut_customsBigData',
        icon: JumpOutCustomBigDataIcon,
        tag: getIn18Text('HAIGUANSHUJU'),
        name: 'jumpOut_customsBigData',
    },
    {
        activeKey: 'jumpOut_mailMarketing',
        icon: JumpOutMailMarketingIcon,
        tag: getIn18Text('YOUJIANYINGXIAO'),
        name: 'jumpOut_mailMarketing',
    },
    {
        activeKey: 'jumpOut_website',
        icon: JumpOutWaimaoWebsiteIcon,
        tag: getIn18Text('WAIMAOJIANZHAN'),
        name: 'jumpOut_website',
    },
    {
        activeKey: 'jumpOut_mediaMarketing',
        icon: JumpOutMediaMarketingIcon,
        tag: getIn18Text('SHEMEIYIGNXIAO'),
        name: 'jumpOut_mediaMarketing',
    },
];
const BaseMainLayout = ({ children, l2cCrmSidebarMenuExtra }) => {
    const { hash } = useLocation();
    const dispatch = useAppDispatch();
    const { updateAutoReplyDetail } = useActions(AutoReplyActions);
    const activeKey = useRef('');
    const hashQueryMap = useRef(new Map());
    const switchPageClickRef = useRef(false);
    const [unread, setUnread] = useState(null);
    const [company, setCompany] = useState('');
    const [tabProps, setTabProps] = useState([]);
    const tabPropsString = useMemo(() => tabProps.filter(v => !v.hidden).join(''), [tabProps]);
    const version = useAppSelector(state => state.privilegeReducer.version);
    const [visibleJumpOut, setVisibleJumpOut] = useState(false);
    const [jumpOutModalMemoVisible, setJumpOutModalMemoVisible] = useState(false);
    const isNewUser = process.env.BUILD_ISEDM && useAppSelector(state => state.popupReducer.showNewUserGuide);
    const visibleCustomerManage = (process.env.BUILD_ISLINGHANG || process.env.BUILD_ISEDM) && useAppSelector(state => getIsSomeMenuVisbleSelector(state.privilegeReducer, ['CUSTOMER_MANAGE']));
    const visibleCustomerPromise = process.env.BUILD_ISEDM && useAppSelector(state => getIsSomeMenuVisbleSelector(state.privilegeReducer, ['CUSTOMER_PROMISE']));
    const { setUserAction } = useActions(AutoUnmountReducer);
    const childrenList = useMemo(() => {
        const list = React.Children.toArray(children) || [];
        return list.filter(e => e && React.isValidElement(e));
    }, [children]);
    const pageKeysStr = useMemo(() => {
        if (!childrenList.length) {
            return '';
        }
        return childrenList
            .filter(v => !v.props.hidden)
            .map(item => item.props.name)
            .join(TAB_STR_SEP);
    }, [childrenList]);
    const currentTabTitle = useMemo(() => {
        const checkedTab = tabProps.find(item => item.name === activeKey.current);
        return checkedTab ? checkedTab.tag : '';
    }, [tabPropsString, activeKey.current]);
    const title = useMemo(() => {
        if (isWebmail()) {
            const unreadTitle = unread != null ? `（${unread > 9999 ? '9999+' : unread}封未读）` : '';
            return `${unreadTitle}${company ? `${company}-` : ''}${currentTabTitle}`;
        }
        return `${TITLE_PREFIX}${currentTabTitle ? `-${currentTabTitle}` : ''}`;
    }, [unread, company, currentTabTitle]);
    const switchPage = useCallback((page, forceSwitch, useHashJump = false) => {
        if (page !== activeKey.current || forceSwitch) {
            activeKey.current = page;
            if (isMatchCustomerPerformanceRoute(page)) {
                activeKey.current = L2cCrmPageType.customerPerformance;
            }
            else if (isMatchCustomerManageRoute(page)) {
                activeKey.current = L2cCrmPageType.customerManagement;
            }
            else if (isMatchUnitableCrmHash(page)) {
                activeKey.current = L2cCrmPageType.customerAndBusiness;
            }
            const hashQuery = hashQueryMap.current.get(page);
            const query = hashQuery ? `?${hashQuery}` : '';
            if (useHashJump) {
                if (l2cCrmSidebarMenuExtra && L2cCrmPageTypeSet.has(page)) {
                    window.location.hash = `#/unitable-crm${l2cCrmSidebarMenuExtra.current[page].defaultPath}`;
                }
                else {
                    window.location.hash = `#${page + query}`;
                }
            }
            else {
                if (process.env.BUILD_ISLINGHANG && page === 'setting' && typeof query === 'string' && query.includes('m=customer')) {
                    navigate(`#${page + query}`);
                }
                else {
                    navigateWithSearchQuery(`#${page + query}`);
                }
            }
            dispatch(GlobalActions.setActiveKey(page));
        }
    }, []);
    const onSwitchPageClick = useCallback((page) => {
        switchPageClickRef.current = true;
        const isUniPage = isMatchUnitableCrmHash(page) || L2cCrmPageTypeSet.has(page);
        switchPage(page, false, isUniPage);
    }, []);
    const onSetPageClickTick = useCallback(() => {
        switchPageClickRef.current = false;
    }, []);
    const errorBoundaryReset = useCallback(() => {
        util.reload();
        return false;
    }, []);
    const getIsUniPageVisible = useCallback((isUniPage) => {
        if (!process.env.BUILD_ISEDM && !process.env.BUILD_ISLINGHANG) {
            return false;
        }
        if (!isUniPage) {
            return false;
        }
        const __hash__ = inWindow() ? window.location.hash : '';
        const isCustomerManageRoute = isUniPage && isMatchCustomerManageRoute(__hash__);
        const isCustomerPerformanceRoute = isUniPage && isMatchCustomerPerformanceRoute(__hash__);
        const isUniPageVisible = (isCustomerManageRoute && visibleCustomerManage) || (isCustomerPerformanceRoute && visibleCustomerPromise);
        console.log(isUniPageVisible, 'isUniPageVisible');
        return isUniPageVisible;
    }, [visibleCustomerManage, visibleCustomerPromise]);
    useEffect(() => {
        const spinWrap = (React.createElement("div", { className: `${style.siriusSpinWrap} baselayout-sirius-spin-wrap` },
            React.createElement("i", { className: `${style.siriusSpinIcon} sirius-spin-icon` }),
            React.createElement("span", { className: 'spin-label ' + style.siriusSpinLabel }, getIn18Text('JIAZAIZHONG..'))));
        Spin.setDefaultIndicator(spinWrap);
        autoReplyApi.getMailRulesByAutoReply().then((autoReply) => {
            updateAutoReplyDetail(autoReply);
        });
        const userInfo = systemApi.getCurrentUser();
        if (userInfo != null && userInfo.company !== company) {
            setCompany(userInfo.company);
        }
        if (process.env.BUILD_ISEDM) {
            window.onhashchange = e => {
                console.warn('test log for hash change', e);
            };
        }
    }, []);
    useEffect(() => {
        const eid = eventApi.registerSysEventObserver('writePageDataExchange', {
            func: (ev) => {
                var _a, _b, _c, _d;
                const { _account, eventStrData, eventData = {} } = ev;
                if (eventStrData === 'start') {
                    const { writeType, id } = eventData;
                    let content = '';
                    if (['forward', 'forwardAsAttach'].includes(writeType)) {
                        content = getIn18Text('ZHUANFAYOUJIANSHENG');
                    }
                    else if (['reply', 'replyAll', 'replyAllWithAttach', 'replyWithAttach'].includes(writeType)) {
                        content = getIn18Text('HUIFUYOUJIANSHENG');
                    }
                    if (content && id) {
                        message.loading({ content, duration: 35, key: ev.eventData.id });
                    }
                }
                else if (eventStrData === 'writeTabCreated') {
                    if ((_a = eventData.entry) === null || _a === void 0 ? void 0 : _a.id) {
                        setTimeout(() => {
                            message.destroy(eventData.entry.id);
                        }, 0);
                    }
                }
                else if (eventStrData === 'sending') {
                    message.loading({
                        content: getIn18Text('XINJIANFASONGZHONG'),
                        duration: 35,
                        key: ev.eventData,
                    });
                }
                else if (eventStrData === 'sendSucceed') {
                    let contentStr = 'XINJIANFASONGCHENG';
                    const sendRevokeRes = dataStoreApi.getSync('sendRevoke');
                    const sendRevokeOpen = sendRevokeRes.suc && sendRevokeRes.data === 'ON';
                    if (sendRevokeOpen) {
                        contentStr = 'XINJIANFASONGCHENGKECHEXIAO';
                    }
                    message.success({
                        content: getIn18Text(contentStr),
                        duration: 2.5,
                        key: ev.eventData,
                    });
                }
                else if (eventStrData === 'scheduleDateSucceed') {
                    message.success({
                        content: getIn18Text('DINGSHIRENWUSHE'),
                        duration: 2.5,
                        key: ev.eventData,
                    });
                }
                else if (ev.eventStrData === 'sendFailed') {
                    setTimeout(() => {
                        message.destroy();
                    }, 0);
                }
                else if (ev.eventStrData === 'createFail') {
                    console.error('createFail', ev);
                    message.error({ content: ((_b = ev === null || ev === void 0 ? void 0 : ev.eventData) === null || _b === void 0 ? void 0 : _b.failMsg) || getIn18Text('XINJIANCHUANGJSB'), duration: 2, key: Math.random() + '' });
                    ((_d = (_c = ev.eventData) === null || _c === void 0 ? void 0 : _c.entry) === null || _d === void 0 ? void 0 : _d.id) && message.destroy(ev.eventData.entry.id);
                }
            },
        });
        return () => {
            eventApi.unregisterSysEventObserver('writePageDataExchange', eid);
        };
    }, []);
    const showSchedule = useFeatureVisibility('sidebar_schedule_forbidden_domains');
    const showDocument = useFeatureVisibility('sidebar_document_forbidden_domains');
    useEffect(() => {
        if (!React.Children.toArray(children).length) {
            return;
        }
        const originTabs = React.Children.toArray(children)
            .filter(child => !child.props.hideInTab && !child.props.hidden)
            .map(({ props }) => ({
            name: props.name,
            tag: props.tag,
            icon: props.icon,
            active: props.active,
            reshow: props.reshow,
            hidden: props.hidden,
            hideInTab: props.hideInTab,
            redPoint: props.redPoint,
            targetUrl: props['data-targetUrl'],
        }));
        if (visibleJumpOut) {
            setTabProps(prevTabs => {
                let tabConfig = prevTabs || [];
                const exist = prevTabs.some(v => v.name === EXTRA_TABS[0].name);
                if (!exist) {
                    trackApi.track('pc_leftNavigationBarTab_waimaoShow');
                    tabConfig = [...prevTabs, ...EXTRA_TABS];
                }
                if (!showSchedule) {
                    tabConfig = tabConfig.filter(item => item.name !== 'schedule');
                }
                if (!showDocument) {
                    tabConfig = tabConfig.filter(item => item.name !== 'disk');
                }
                return tabConfig;
            });
        }
        else if (version === 'WEBSITE' && !process.env.BUILD_ISLINGXI) {
            const baseTabs = ['message', 'mailbox', 'site'];
            const targetTabs = [...baseTabs, L2cCrmPageType.customerManagement, L2cCrmPageType.customerPerformance];
            let websiteTabProps = originTabs.filter(tab => targetTabs.includes(tab.name));
            if (!showSchedule) {
                websiteTabProps = websiteTabProps.filter(item => item.name !== 'schedule');
            }
            if (!showDocument) {
                websiteTabProps = websiteTabProps.filter(item => item.name !== 'disk');
            }
            setTabProps(websiteTabProps);
        }
        else {
            let tabConfig = [...originTabs];
            if (!showSchedule) {
                tabConfig = tabConfig.filter(item => item.name !== 'schedule');
            }
            if (!showDocument) {
                tabConfig = tabConfig.filter(item => item.name !== 'disk');
            }
            setTabProps(tabConfig);
        }
    }, [
        React.Children.toArray(children).filter(item => React.isValidElement(item) && !lodashGet(item, 'props.hidden', false)).length,
        version,
        visibleJumpOut,
        showDocument,
        showSchedule,
    ]);
    useEffect(() => {
        const pageKeys = pageKeysStr.split(TAB_STR_SEP);
        const [pageWithHash, query] = hash.split('?');
        const [, page] = pageWithHash.split('#');
        const isUniPage = isMatchUnitableCrmHash(page);
        if (query) {
            hashQueryMap.current.set(page, query);
        }
        if (isUniPage) {
            hashQueryMap.current.set(page, query);
        }
        if ((page === 'edm' || page === 'intelliMarketing') && !query) {
            hashQueryMap.current.set(page, query);
        }
        const emptyPage = !pageKeys.includes(page) && !getIsUniPageVisible(isUniPage);
        if (!page || emptyPage) {
            const defaultPage = pageKeys[0];
            if (defaultPage) {
                switchPage(defaultPage, emptyPage, false);
                trackApi.track('pc_leftNavigationBarTab', { tabName: defaultPage, operate: 'default' });
            }
        }
        else {
            switchPage(page, false, isUniPage);
        }
    }, [hash, pageKeysStr, getIsUniPageVisible]);
    useEffect(() => {
        const [pageWithHash, query] = hash.split('?');
        const [, page] = pageWithHash.split('#');
        const params = new URLSearchParams(hash.split('?')[1]);
        const tabKey = params.get('page');
        tabKey && setUserAction({ module: page, page: tabKey });
    }, [hash]);
    useEffect(() => {
        if (isLingxiWeb) {
            loginApi
                .getEntranceVisibleConfig()
                .then(visibleConfig => {
                if (visibleConfig.showTab) {
                    setVisibleJumpOut(true);
                }
                if (visibleConfig.showPopup) {
                    setJumpOutModalMemoVisible(true);
                    trackApi.track('pc_waimaoAttracts_show');
                }
            })
                .catch(e => {
                console.error('getEntranceVisibleConfig error', e);
            });
        }
    }, []);
    useEffect(() => {
        if (process.env.BUILD_ISEDM) {
            loginApi.reportEdmLogin();
        }
    }, []);
    useEventObserver('notificationChange', {
        name: 'navbarNotificationChangeOb',
        func: ev => {
            if (ev.eventStrData === 'mail') {
                setUnread(ev.eventData);
            }
        },
    });
    useEventObserver('updateUserInfo', {
        name: 'changeTitle',
        func: ev => {
            if (ev.eventStrData) {
                const { company: companyName } = ev.eventData;
                setCompany(companyName !== null && companyName !== void 0 ? companyName : '');
            }
        },
    });
    const onHandleJumpOutClick = useCallback((page) => {
        trackApi.track('pc_leftNavigationBarTab_waimaoClick');
        loginApi
            .getLoginCode()
            .then(code => {
            var _a;
            if (code) {
                const redirectUrl = getJumpOutRedirectUrl((_a = systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.sessionId, page);
                const jumpUrl = getWaimaoTrailJumpUrl(code, redirectUrl);
                window.location.assign(jumpUrl);
            }
            else {
                SiriusMessage.warn({ content: getIn18Text('TIAOZHUANSHIBAI，QINGSHAO') }).then();
            }
        })
            .catch(e => {
            SiriusMessage.warn({ content: typeof e === 'string' ? e : getIn18Text('TIAOZHUANSHIBAI，QINGSHAO') }).then();
        });
    }, []);
    const isSiteLoading = useRef(false);
    const onHandleSiteManageClick = useCallback(async (tab) => {
        var _a;
        trackApi.track('pc_leftNavigationBarTab', { tabName: '建站导流入口', operate: 'click' });
        const { targetUrl } = tab;
        console.log('onHandleSiteManageClick targetUrl', targetUrl);
        if (isSiteLoading.current) {
            return;
        }
        try {
            isSiteLoading.current = true;
            const success = await accountApi.doCreateWebsiteOrder();
            if (success) {
                const code = await loginApi.getLoginCode();
                if (code && targetUrl) {
                    const host = getWaimaoEntryHost();
                    const sid = (_a = systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.sessionId;
                    const query = `jumpMode=clientLogin&sid=${sid}&from=${encodeURIComponent(window.location.href)}&target=${encodeURIComponent(targetUrl)}`;
                    const url = window.location.hash.includes('static/sirius-web') ? `static/sirius-web/jump/?${query}` : `jump/?${query}`;
                    const redirectUrl = host + encodeURIComponent(url);
                    const path = 'it-others/api/pub/login/jump/';
                    const jumpUrl = `${host}${path}?code=${code}&redirectUrl=${redirectUrl}`;
                    window.open(jumpUrl);
                }
                else {
                    SiriusMessage.warn(getIn18Text('TIAOZHUANSHIBAI，QINGSHAO'));
                }
            }
            else {
                SiriusMessage.warn(getIn18Text('TIAOZHUANSHIBAI，QINGSHAO'));
            }
        }
        catch (error) {
            console.error('onHandleSiteManageClick error', error);
            SiriusMessage.warn(getIn18Text('TIAOZHUANSHIBAI，QINGSHAO'));
        }
        isSiteLoading.current = false;
    }, []);
    const SdeTabBarCompMemo = useMemo(() => {
        if (tabProps.length === 0) {
            return null;
        }
        return (React.createElement(SideTabBar, { activeKey: activeKey.current, onChange: onSwitchPageClick, tabs: tabProps, handleJumpOutClick: onHandleJumpOutClick, handleSiteManageClick: onHandleSiteManageClick }));
    }, [tabPropsString, activeKey.current]);
    const onJumpOutCancel = () => {
        setJumpOutModalMemoVisible(false);
    };
    const onJumpOutConfirm = () => {
        setJumpOutModalMemoVisible(false);
        onHandleJumpOutClick('globalSearch');
    };
    const JumpOutModalMemo = useMemo(() => {
        if (isLingxiWeb) {
            return React.createElement(JumpOutModal, { visible: jumpOutModalMemoVisible, onJumpOutCancel: onJumpOutCancel, onJumpOutConfirm: onJumpOutConfirm });
        }
        return null;
    }, [jumpOutModalMemoVisible, setJumpOutModalMemoVisible]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: classNames(style.mainLayoutContainer, activeKey.current === 'message' ? style.forbidSelect : {}) },
            React.createElement(Helmet, null,
                React.createElement("meta", { charSet: "utf-8" }),
                React.createElement("title", null, title)),
            SdeTabBarCompMemo,
            React.createElement(GlobalAdSpace, null),
            isNewUser ? React.createElement(NewbieTaskPopup, null) : React.createElement(React.Fragment, null),
            React.createElement(ErrorBoundary, { name: "baseLayout", onReset: errorBoundaryReset },
                React.createElement(LayoutPerf, { activeKey: activeKey.current, tickStart: switchPageClickRef.current, setClickTick: onSetPageClickTick }, children)),
            JumpOutModalMemo)));
};
export default BaseMainLayout;
